import { useState } from 'react';
import { defaultAmenities } from '@sparklib';
import { Row, Col, Label } from 'reactstrap';

const TEAL = '#5E6E8F';

export default function Amenities({
  getFilter,
  addFilter,
  style,
  activeFilterPill,
}) {
  const [search, setSearch] = useState('');

  const included = getFilter('amenities') || [];
  const excluded = getFilter('excludeAmenities') || [];

  // Count how many defaultAmenities categories have a state
  const includedCount = defaultAmenities.filter((a) =>
    included.find((s) => a.search.includes(s.label))
  ).length;
  const excludedCount = defaultAmenities.filter((a) =>
    excluded.find((s) => a.search.includes(s.label))
  ).length;

  function getState(addon) {
    if (included.find((a) => addon.search.includes(a.label))) return 'include';
    if (excluded.find((a) => addon.search.includes(a.label))) return 'exclude';
    return null;
  }

  const filtered = defaultAmenities.filter((addon) =>
    addon.name.toLowerCase().includes(search.toLowerCase())
  );

  const sorted = [
    ...filtered.filter((a) => getState(a) === 'include'),
    ...filtered.filter((a) => getState(a) === 'exclude'),
    ...filtered.filter((a) => getState(a) === null),
  ];

  function handleChange(addon) {
    const current = getState(addon);
    const terms = addon.search.map((t) => ({ label: t }));

    if (current === null) {
      // null → include: add to included, ensure not in excluded
      addFilter({
        e: [...included.filter((a) => !addon.search.includes(a.label)), ...terms],
        key: 'amenities',
      });
    } else if (current === 'include') {
      // include → exclude: remove from included, add to excluded
      addFilter({
        e: included.filter((a) => !addon.search.includes(a.label)),
        key: 'amenities',
      });
      addFilter({
        e: [...excluded.filter((a) => !addon.search.includes(a.label)), ...terms],
        key: 'excludeAmenities',
      });
    } else {
      // exclude → null: remove from excluded
      addFilter({
        e: excluded.filter((a) => !addon.search.includes(a.label)),
        key: 'excludeAmenities',
      });
    }
  }

  function clearAll() {
    addFilter({ e: [], key: 'amenities' });
    addFilter({ e: [], key: 'excludeAmenities' });
  }

  return (
    <div
      className={`filter-box-container ${activeFilterPill !== 'amenities' && 'hidden'}`}
    >
      <div className='filter-box amenities-filter-box' style={style}>
        {/* Header */}
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <h6 style={{ display: 'inline-block' }}>Amenities</h6>

          {/* Selected counts */}
          {(includedCount > 0 || excludedCount > 0) && (
            <>
              <div
                style={{
                  color: TEAL,
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  marginBottom: 8,
                  position: 'absolute',
                  top: 0,
                  right: 30,
                  display: 'flex',
                  gap: 6,
                }}
              >
                {includedCount > 0 && (
                  <span style={{ color: '#28a745' }}>{includedCount} included</span>
                )}
                {excludedCount > 0 && (
                  <span style={{ color: '#dc3545' }}>{excludedCount} excluded</span>
                )}
              </div>

              <button
                onClick={clearAll}
                title='Clear all'
                style={{
                  position: 'absolute',
                  top: -2,
                  right: 0,
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  background: TEAL,
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                <span style={{ color: '#fff', fontSize: 14, lineHeight: 1 }}>✕</span>
              </button>
            </>
          )}
        </div>

        <Row className='mt-2'>
          <Col md='12'>
            <div className={'search-bar'} style={{ margin: '0 0 10px 0' }}>
              <div className={'mb-0 d-inline-flex flex-grow-1 align-items-center form-group'}>
                <Label for='amenity-search' className={'mb-0 w-auto d-inline-flex mr-1'}>
                  <svg aria-hidden='true' focusable='false' data-prefix='fas' data-icon='search' className='svg-inline--fa fa-search fa-w-16' role='img' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'>
                    <path fill='currentColor' d='M505 442.7L405.3 343c-4.5-4.5-10.6-7-17-7H372c27.6-35.3 44-79.7 44-128C416 93.1 322.9 0 208 0S0 93.1 0 208s93.1 208 208 208c48.3 0 92.7-16.4 128-44v16.3c0 6.4 2.5 12.5 7 17l99.7 99.7c9.4 9.4 24.6 9.4 33.9 0l28.3-28.3c9.4-9.4 9.4-24.6.1-34zM208 336c-70.7 0-128-57.2-128-128 0-70.7 57.2-128 128-128 70.7 0 128 57.2 128 128 0 70.7-57.2 128-128 128z' />
                  </svg>
                </Label>
                <input
                  id='amenity-search'
                  name='search'
                  type='search'
                  placeholder='Search amenities...'
                  value={search}
                  autoComplete='off'
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    border: 'none',
                    outline: 'none',
                    width: '100%',
                    color: '#333',
                    background: 'transparent',
                  }}
                />
              </div>
            </div>

            {/* List */}
            <div
              title='Click once to include, again to exclude, again to clear'
              style={{
                maxHeight: 240,
                overflowY: 'scroll',
                border: '1px solid #bdc3d7',
                borderRadius: 5,
                padding: '10px 10px',
              }}
            >
              {sorted.length > 0 ? sorted.map((addon) => {
                const state = getState(addon);
                return (
                  <div
                    key={addon.name}
                    style={{
                      display: 'flex',
                      padding: '0px 10px',
                      marginBottom: 2,
                    }}
                  >
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        userSelect: 'none',
                        cursor: 'pointer',
                        width: '100%',
                      }}
                      onClick={() => handleChange(addon)}
                    >
                      {/* Tri-state indicator */}
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 16,
                          height: 16,
                          borderRadius: 3,
                          marginRight: 8,
                          flexShrink: 0,
                          border: state === null ? '1.5px solid #aaa' : 'none',
                          background:
                            state === 'include'
                              ? '#28a745'
                              : state === 'exclude'
                              ? '#dc3545'
                              : 'transparent',
                          color: '#fff',
                          fontSize: 11,
                          lineHeight: 1,
                        }}
                      >
                        {state === 'include' && '✓'}
                        {state === 'exclude' && '✕'}
                      </span>
                      {addon.name}
                      {addon.popular && state === null && (
                        <span
                          style={{ color: TEAL, marginLeft: 6, fontSize: '0.8rem' }}
                        >
                          ★
                        </span>
                      )}
                    </label>
                  </div>
                );
              }) : (
                <div style={{ fontSize: '0.9rem', padding: '2px 5px' }}>
                  No amenities match your search
                </div>
              )}
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
}
